import { useEffect, useState } from 'react';

/**
 * 読込タイムアウト hook (Sprint AM: UI2 無限スピナー解消、docs/16 §10-2 P1 H4)。
 *
 * `active=true` の状態が `timeoutMs` (default 30,000) 経過したら `true` を返す。
 * `active=false` に戻れば即 reset (false)。
 *
 * 想定 UX:
 *   - hook の `status === 'loading'` を `active` に渡す
 *   - 戻り値 false: 通常通り <Spinner /> を表示
 *   - 戻り値 true: spinner を隠し「読込が遅れています — 再試行」banner を露出
 *
 * 注意:
 *   - timeoutMs を mid-flight で変更しても、現在の timer を打ち切って張り直す
 *   - cleanup で必ず timer を clear (memory leak / cross-mount fire 防止)
 *   - SSR safe (`useState` initial = false、`useEffect` 内で setTimeout)
 */
export function useLoadingTimeout(active: boolean, timeoutMs: number = 30_000): boolean {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!active) {
      // active=false に戻ったら即 reset
      setTimedOut(false);
      return;
    }
    // 念のため timer 開始時にも false に揃える (再 mount / active 立て直し対策)
    setTimedOut(false);
    const id = setTimeout(() => {
      setTimedOut(true);
    }, timeoutMs);
    return () => {
      clearTimeout(id);
    };
  }, [active, timeoutMs]);

  return timedOut;
}
